import { SpaceSelectCompassComponent } from "@circle-react-shared/SpaceSelectCompass/SpaceSelectCompassComponent";
import { Form } from "@circle-react-uikit/Form";
import {
  SPACE_SELECT_DROPDOWN_MAX_HEIGHT,
  SPACE_SELECT_DROPDOWN_MAX_WIDTH,
} from "@circle-react/components/QuickPostV3/constants";
import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { CommunityMemberSelectCompass } from "@circle-react/components/shared/CommunityMemberSelectCompass";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { isPublishedPost } from "@circle-react/helpers/postHelpers";
import { userIsAdminOrModeratorOfSpace } from "@circle-react/helpers/spaceHelpers";
import { FormField } from "@circleco/compass/components/FormField";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Typography } from "@circleco/compass/components/Typography";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { useIsTruncated } from "@circleco/compass/hooks/useIsTruncated";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import { PublishTimeInput } from "./PublishTimeInput";
import { SettingsSection } from "./SettingsSection";
import { SettingsSectionBody } from "./SettingsSectionBody";

const usePostSlugPrefixes = () => {
  const { space } = useComposerSpace();

  if (typeof window === "undefined") {
    return { pathPrefix: "" };
  }

  const spaceSlug = space?.slug;
  const pathPrefix = spaceSlug ? `/c/${spaceSlug}/` : "/c/";

  return { pathPrefix };
};

export const PublishingSection = () => {
  const isMobile = useIsMobile();
  const { control, watch } = useFormContext<PostFormValues>();
  const { space } = useComposerSpace();
  const { pathPrefix } = usePostSlugPrefixes();
  const pathPrefixRef = useRef<HTMLSpanElement>(null);
  const isPathPrefixTruncated = useIsTruncated(pathPrefixRef);
  const {
    helpers: { basicSpacesWithPostCreationAllowed },
  } = useSpacesContext();
  const [postId, status, slug] = watch(["id", "status", "slug"]);
  const spaces = basicSpacesWithPostCreationAllowed();
  const canChangeAuthor = space ? userIsAdminOrModeratorOfSpace(space) : false;
  const isEditingPublishedPost = Boolean(postId) && isPublishedPost({ status });

  return (
    <SettingsSection title={t("quick_post_v3.settings.sections.publishing")}>
      <SettingsSectionBody>
        {isEditingPublishedPost && <PublishTimeInput />}
        <FormField
          layout={isMobile ? "vertical" : "horizontal"}
          horizontalLayoutRatio="40/60"
          label={t("post.space_id")}
        >
          <Controller
            control={control}
            name="space_id"
            render={({ field }) => (
              <SpaceSelectCompassComponent
                spaces={spaces}
                value={field.value ?? null}
                dropdownMaxWidth={SPACE_SELECT_DROPDOWN_MAX_WIDTH}
                dropdownMaxHeight={SPACE_SELECT_DROPDOWN_MAX_HEIGHT}
                onChange={val => {
                  if (val == null && field.value != null) {
                    return;
                  }
                  field.onChange(val);
                }}
              />
            )}
          />
        </FormField>
        <FormField
          layout={isMobile ? "vertical" : "horizontal"}
          horizontalLayoutRatio="40/60"
          label={t("post.slug")}
        >
          <Form.Input
            name="slug"
            placeholder={
              slug ? undefined : t("quick_post_v3.settings.slug_placeholder")
            }
            prefix={
              <Tooltip content={pathPrefix} disabled={!isPathPrefixTruncated}>
                <Typography
                  ref={pathPrefixRef}
                  variant="body-sm"
                  color="tertiary"
                  component="span"
                  className="block min-w-0"
                  truncate
                >
                  {pathPrefix}
                </Typography>
              </Tooltip>
            }
            prefixWrapperClassName="min-w-0 overflow-hidden"
          />
        </FormField>
        {canChangeAuthor && (
          <FormField
            layout={isMobile ? "vertical" : "horizontal"}
            horizontalLayoutRatio="40/60"
            label={t("post.user_id")}
          >
            <Controller
              control={control}
              name="user_id"
              render={({ field }) => (
                <CommunityMemberSelectCompass
                  value={field.value ?? null}
                  valueField="user_id"
                  onChange={field.onChange}
                />
              )}
            />
          </FormField>
        )}
      </SettingsSectionBody>
    </SettingsSection>
  );
};
